import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import Navigation from '../components/Navigation';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Wallet, Plus, ArrowUpRight, ArrowDownRight, IndianRupee, CreditCard, ShieldCheck, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const WalletPage = () => {
  const { user } = useAuth();
  const [wallet, setWallet] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [addAmount, setAddAmount] = useState('');
  const [paying, setPaying] = useState(false);

  // Cleanup Razorpay body styles on mount and after navigation
  const cleanupRazorpayStyles = () => {
    document.body.style.removeProperty('pointer-events');
    document.body.style.removeProperty('overflow');
    document.body.style.removeProperty('contain');
  };

  useEffect(() => {
    cleanupRazorpayStyles();
    fetchWalletData();
  }, []);

  const fetchWalletData = async () => {
    try {
      const [walletRes, txnRes] = await Promise.all([
        axios.get(`${API}/wallet`),
        axios.get(`${API}/wallet/transactions`)
      ]);
      setWallet(walletRes.data);
      setTransactions(txnRes.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const loadRazorpayScript = useCallback(() => {
    return new Promise((resolve) => {
      if (document.querySelector('script[src="https://checkout.razorpay.com/v1/checkout.js"]')) {
        resolve(true);
        return;
      }
      const script = document.createElement('script');
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });
  }, []);

  const handlePayWithRazorpay = async (e) => {
    e.preventDefault();
    const amount = parseFloat(addAmount);
    if (!amount || amount <= 0) {
      toast.error('Enter a valid amount');
      return;
    }

    setPaying(true);

    try {
      // Step 1: Load Razorpay script
      const scriptLoaded = await loadRazorpayScript();
      if (!scriptLoaded) {
        toast.error('Failed to load payment gateway. Please check your internet connection.');
        setPaying(false);
        return;
      }

      // Step 2: Create Razorpay order on backend
      const orderRes = await axios.post(`${API}/wallet/create-order`, { amount });
      const { order_id, amount: amountPaise, currency, key_id } = orderRes.data;

      // Step 3: Open Razorpay checkout
      const options = {
        key: key_id,
        amount: amountPaise,
        currency: currency,
        name: 'TaskPiece',
        description: `Wallet Top-up: ₹${amount.toLocaleString('en-IN')}`,
        order_id: order_id,
        handler: async (response) => {
          // Step 4: Verify payment on backend
          try {
            const verifyRes = await axios.post(`${API}/wallet/verify-payment`, {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature
            });
            toast.success(verifyRes.data.message || `₹${amount.toLocaleString('en-IN')} added to wallet!`);
            setShowAddDialog(false);
            setAddAmount('');
            fetchWalletData();
          } catch (verifyErr) {
            toast.error(verifyErr.response?.data?.detail || 'Payment verification failed. Contact support if money was deducted.');
          }
          setPaying(false);
          cleanupRazorpayStyles();
        },
        prefill: {
          name: user?.full_name || '',
          email: user?.email || '',
        },
        theme: {
          color: '#1a1a2e'
        },
        modal: {
          ondismiss: () => {
            setPaying(false);
            cleanupRazorpayStyles();
            toast.info('Payment cancelled');
          }
        }
      };

      const rzp = new window.Razorpay(options);
      rzp.on('payment.failed', (response) => {
        setPaying(false);
        cleanupRazorpayStyles();
        toast.error(`Payment failed: ${response.error?.description || 'Unknown error'}`);
      });
      rzp.open();
    } catch (err) {
      setPaying(false);
      const detail = err.response?.data?.detail;
      if (detail?.includes('not configured')) {
        toast.error('Payment gateway is not configured. Please ask the admin to set up Razorpay credentials.');
      } else {
        toast.error(detail || 'Failed to initiate payment. Please try again.');
      }
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
        <Navigation />
        <div className="flex items-center justify-center h-96">Loading wallet...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      <Navigation />
      <div className="max-w-4xl mx-auto px-6 md:px-12 py-12">
        <h1 className="text-3xl md:text-4xl font-heading font-bold text-primary mb-8" data-testid="wallet-title">
          My Wallet
        </h1>

        {/* Balance Card */}
        <Card className="mb-8 bg-gradient-to-r from-primary to-primary/80 text-primary-foreground">
          <CardContent className="pt-8 pb-8">
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div>
                <p className="text-sm opacity-80">Available Balance</p>
                <div className="text-4xl font-heading font-bold mt-1 flex items-center gap-2" data-testid="wallet-balance">
                  <IndianRupee className="w-8 h-8" />
                  {(wallet?.balance || 0).toLocaleString('en-IN')}
                </div>
              </div>
              <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
                <DialogTrigger asChild>
                  <Button variant="secondary" size="lg" className="bg-white text-primary hover:bg-white/90" data-testid="add-money-button">
                    <Plus className="w-5 h-5 mr-2" /> Add Money
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                      <CreditCard className="w-5 h-5" /> Add Money to Wallet
                    </DialogTitle>
                    <DialogDescription>Pay securely via Razorpay</DialogDescription>
                  </DialogHeader>
                  <form onSubmit={handlePayWithRazorpay} className="space-y-4" data-testid="add-money-form">
                    <div>
                      <Label>Amount (₹)</Label>
                      <Input
                        type="number"
                        min="1"
                        max="500000"
                        value={addAmount}
                        onChange={(e) => setAddAmount(e.target.value)}
                        placeholder="Enter amount"
                        required
                        data-testid="add-money-amount"
                      />
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {[500, 1000, 2000, 5000].map(amt => (
                        <Button
                          key={amt}
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => setAddAmount(String(amt))}
                          data-testid={`preset-amount-${amt}`}
                        >
                          ₹{amt.toLocaleString('en-IN')}
                        </Button>
                      ))}
                    </div>
                    <div className="flex items-center gap-2 text-xs text-muted-foreground bg-slate-50 p-2.5 rounded-lg">
                      <ShieldCheck className="w-4 h-4 text-green-600 flex-shrink-0" />
                      <span>Secured by Razorpay. Supports UPI, cards, net banking & wallets.</span>
                    </div>
                    <Button type="submit" className="w-full h-11" disabled={paying || !addAmount} data-testid="add-money-submit">
                      <CreditCard className="w-4 h-4 mr-2" />
                      {paying ? 'Processing...' : `Pay ₹${addAmount ? parseFloat(addAmount).toLocaleString('en-IN') : '0'}`}
                    </Button>
                  </form>
                </DialogContent>
              </Dialog>
            </div>
          </CardContent>
        </Card>

        {/* Transaction History */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2"><Wallet className="w-5 h-5" /> Transaction History</CardTitle>
            <CardDescription>Your recent wallet activity</CardDescription>
          </CardHeader>
          <CardContent>
            {transactions.length === 0 ? (
              <p className="text-center text-muted-foreground py-8">No transactions yet. Add money to get started!</p>
            ) : (
              <div className="space-y-2">
                {transactions.map(txn => (
                  <div key={txn.id} className="flex items-center gap-4 py-3 border-b last:border-0" data-testid={`txn-${txn.id}`}>
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${txn.type === 'credit' ? 'bg-green-100' : 'bg-red-100'}`}>
                      {txn.type === 'credit' ? <ArrowUpRight className="w-5 h-5 text-green-600" /> : <ArrowDownRight className="w-5 h-5 text-red-600" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium">{txn.reason}</p>
                      <p className="text-xs text-muted-foreground">
                        by {txn.performed_by_name} - {new Date(txn.created_at).toLocaleString('en-IN')}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className={`font-heading font-bold ${txn.type === 'credit' ? 'text-green-600' : 'text-red-600'}`}>
                        {txn.type === 'credit' ? '+' : '-'}₹{txn.amount?.toLocaleString('en-IN')}
                      </p>
                      <p className="text-xs text-muted-foreground">Bal: ₹{txn.balance_after?.toLocaleString('en-IN')}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default WalletPage;
